/**
 * G2eGente - Gestão de Turmas de Treinamento
 * Listagem e gestão de turmas criadas
 */
import React, { useState, useEffect } from 'react';
import { turmasAPI, trainingsAPI, employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users, 
  Calendar,
  CheckCircle,
  Clock,
  Loader2,
  Search,
  Eye,
  UserPlus,
  CheckCheck,
  X,
  AlertCircle,
  DollarSign,
  Edit,
  Save,
  Play,
  Plus,
  Trash2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'aberta': 'bg-green-100 text-green-700',
  'em_andamento': 'bg-blue-100 text-blue-700',
  'concluida': 'bg-slate-100 text-slate-700',
  'cancelada': 'bg-red-100 text-red-700'
};

const STATUS_LABELS = {
  'aberta': 'Aberta',
  'em_andamento': 'Em Andamento',
  'concluida': 'Concluída',
  'cancelada': 'Cancelada'
};

const PARTICIPANTE_STATUS_COLORS = {
  'inscrito': 'bg-blue-100 text-blue-700',
  'confirmado': 'bg-green-100 text-green-700',
  'presente': 'bg-emerald-100 text-emerald-700',
  'ausente': 'bg-red-100 text-red-700',
  'concluido': 'bg-slate-100 text-slate-700'
};

export default function TurmasTab() {
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTurma, setSelectedTurma] = useState(null);
  const [turmaDetail, setTurmaDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Add participant modal
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [addingParticipant, setAddingParticipant] = useState(false);
  
  // Conclude modal
  const [showConcludeModal, setShowConcludeModal] = useState(false);
  const [presentes, setPresentes] = useState([]);
  const [concluding, setConcluding] = useState(false);
  
  // Edit mode for turma details
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    data_inicio: '',
    data_fim: '',
    instrutor: '',
    valor_treinamento: '',
    valor_hora_total: '',
    // Campos de treinamento online
    modalidade: 'presencial',
    video_youtube_url: '',
    percentual_minimo: 80,
    prova_ativa: false,
    nota_minima: 70,
    questoes: []
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Modal de questões
  const [showQuestoesModal, setShowQuestoesModal] = useState(false);
  const [editingQuestao, setEditingQuestao] = useState(null);
  const [questaoForm, setQuestaoForm] = useState({
    pergunta: '',
    alternativas: ['', '', '', ''],
    resposta_correta: 0
  });

  useEffect(() => {
    fetchTurmas();
  }, []);

  const fetchTurmas = async () => {
    try {
      const res = await turmasAPI.getAll();
      setTurmas(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar turmas');
    } finally {
      setLoading(false);
    }
  };

  const fetchTurmaDetail = async (turma) => {
    setSelectedTurma(turma);
    setShowDetailModal(true);
    setLoadingDetail(true);
    setIsEditing(false);
    try {
      const res = await turmasAPI.getById(turma.id);
      setTurmaDetail(res.data);
      // Initialize presentes with current participants
      if (res.data.participantes) {
        setPresentes(res.data.participantes
          .filter(p => p.status !== 'ausente')
          .map(p => p.employee_id)
        );
      }
      // Initialize edit form
      setEditForm({
        data_inicio: res.data.data_inicio?.split('T')[0] || '',
        data_fim: res.data.data_fim?.split('T')[0] || '',
        instrutor: res.data.instrutor || '',
        valor_treinamento: res.data.valor_treinamento?.toString() || '',
        valor_hora_total: res.data.valor_hora_total?.toString() || '',
        // Campos de treinamento online
        modalidade: res.data.modalidade || 'presencial',
        video_youtube_url: res.data.video_youtube_url || '',
        percentual_minimo: res.data.percentual_minimo || 80,
        prova_ativa: res.data.prova_ativa || false,
        nota_minima: res.data.nota_minima || 70,
        questoes: res.data.questoes || []
      });
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    } finally {
      setLoadingDetail(false);
    }
  };
  
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      await turmasAPI.update(selectedTurma.id, {
        data_inicio: editForm.data_inicio || null,
        data_fim: editForm.data_fim || null,
        instrutor: editForm.instrutor || null,
        valor_treinamento: editForm.valor_treinamento ? parseFloat(editForm.valor_treinamento) : null,
        valor_hora_total: editForm.valor_hora_total ? parseFloat(editForm.valor_hora_total) : null,
        // Campos de treinamento online
        modalidade: editForm.modalidade,
        video_youtube_url: editForm.video_youtube_url || null,
        percentual_minimo: editForm.percentual_minimo,
        prova_ativa: editForm.prova_ativa,
        nota_minima: editForm.nota_minima,
        questoes: editForm.questoes
      });
      toast.success('Turma atualizada com sucesso');
      setIsEditing(false);
      // Refresh detail
      const res = await turmasAPI.getById(selectedTurma.id);
      setTurmaDetail(res.data);
      fetchTurmas();
    } catch (error) {
      toast.error('Erro ao salvar alterações');
    } finally {
      setSavingEdit(false);
    }
  };
  
  // Função para gerar PDF de Lista de Presença
  const handleGenerateListaPresenca = async () => {
    try {
      toast.info('Gerando lista de presença...');
      
      // Buscar dados da lista de presença
      const res = await turmasAPI.getListaPresenca(selectedTurma.id);
      const dados = res.data;
      
      // Importar jsPDF dinamicamente
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let y = margin;
      
      // Carregar logo se existir
      let logoLoaded = false;
      if (dados.empresa.logo_url) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = dados.empresa.logo_url;
          });
          doc.addImage(img, 'PNG', margin, y, 30, 20);
          logoLoaded = true;
        } catch (e) {
          console.warn('Não foi possível carregar o logo:', e);
        }
      }
      
      // Cabeçalho
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const titulo = 'LISTA DE PRESENÇA (TREINAMENTOS)';
      const tituloWidth = doc.getTextWidth(titulo);
      doc.text(titulo, (pageWidth - tituloWidth) / 2, y + (logoLoaded ? 10 : 5));
      
      y += logoLoaded ? 30 : 15;
      
      // Linha separadora
      doc.setDrawColor(0, 150, 180);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 8;
      
      // Informações do treinamento
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Treinamento:', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(dados.treinamento.nome || '-', margin + 30, y);
      y += 6;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Carga Horária:', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(dados.treinamento.carga_horaria ? `${dados.treinamento.carga_horaria} horas` : '-', margin + 33, y);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Modalidade:', pageWidth / 2, y);
      doc.setFont('helvetica', 'normal');
      const modalidadeLabel = {
        'presencial': 'Presencial',
        'online': 'Online',
        'hibrido': 'Híbrido'
      }[dados.turma.modalidade] || 'Presencial';
      doc.text(modalidadeLabel, pageWidth / 2 + 28, y);
      y += 6;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Instrutor:', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(dados.turma.instrutor || '-', margin + 22, y);
      y += 6;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Data:', margin, y);
      doc.setFont('helvetica', 'normal');
      // Para turma concluída, usar data de conclusão se disponível
      const dataExibir = dados.turma.status === 'concluida' && dados.turma.data_conclusao
        ? dados.turma.data_conclusao
        : dados.turma.data_inicio;
      const dataFormatada = dataExibir 
        ? new Date(dataExibir).toLocaleDateString('pt-BR')
        : '___/___/______';
      doc.text(dataFormatada, margin + 14, y);
      
      // Mostrar período se houver data de fim diferente
      if (dados.turma.data_inicio && dados.turma.data_fim && dados.turma.data_fim !== dados.turma.data_inicio) {
        doc.setFont('helvetica', 'bold');
        doc.text('Período:', pageWidth / 2, y);
        doc.setFont('helvetica', 'normal');
        doc.text(`${new Date(dados.turma.data_inicio).toLocaleDateString('pt-BR')} a ${new Date(dados.turma.data_fim).toLocaleDateString('pt-BR')}`, pageWidth / 2 + 18, y);
      }
      y += 10;
      
      // Campo para observações (texto livre)
      doc.setFont('helvetica', 'bold');
      doc.text('Observações:', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setDrawColor(150);
      doc.line(margin + 28, y, pageWidth - margin, y);
      y += 6;
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
      
      // Tabela de participantes
      doc.setFillColor(0, 150, 180);
      doc.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      
      // Cabeçalhos da tabela
      const colNome = margin + 2;
      const colPresencial = pageWidth - margin - 85;
      const colOnline = pageWidth - margin - 55;
      const colAssinatura = pageWidth - margin - 25;
      
      doc.text('Nome do Empregado', colNome, y + 5.5);
      
      // Cabeçalho "HORAS TREINAMENTO" centralizado
      const horasHeader = 'HORAS TREINAMENTO';
      doc.setFontSize(7);
      doc.text(horasHeader, colPresencial + 5, y + 3);
      doc.setFontSize(9);
      doc.text('Presencial', colPresencial, y + 7);
      doc.text('Online', colOnline, y + 7);
      doc.text('Assinatura', colAssinatura - 5, y + 5.5);
      
      y += 8;
      doc.setTextColor(0, 0, 0);
      
      // Linhas dos participantes
      const lineHeight = 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      dados.participantes.forEach((p, index) => {
        // Verificar se precisa nova página
        if (y + lineHeight > pageHeight - 20) {
          doc.addPage();
          y = margin;
          
          // Repetir cabeçalho na nova página
          doc.setFillColor(0, 150, 180);
          doc.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.text('Nome do Empregado', colNome, y + 5.5);
          doc.setFontSize(7);
          doc.text('HORAS TREINAMENTO', colPresencial + 5, y + 3);
          doc.setFontSize(9);
          doc.text('Presencial', colPresencial, y + 7);
          doc.text('Online', colOnline, y + 7);
          doc.text('Assinatura', colAssinatura - 5, y + 5.5);
          y += 8;
          doc.setTextColor(0, 0, 0);
        }
        
        // Alternar cor de fundo
        if (index % 2 === 0) {
          doc.setFillColor(245, 245, 245);
          doc.rect(margin, y, pageWidth - 2 * margin, lineHeight, 'F');
        }
        
        // Borda da linha
        doc.setDrawColor(200);
        doc.rect(margin, y, pageWidth - 2 * margin, lineHeight);
        
        // Nome do empregado
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const nomeDisplay = p.nome.length > 40 ? p.nome.substring(0, 37) + '...' : p.nome;
        doc.text(nomeDisplay, colNome, y + 6.5);
        
        // Linhas para preenchimento
        doc.setDrawColor(150);
        // Presencial
        doc.line(colPresencial, y + 7, colPresencial + 18, y + 7);
        // Online
        doc.line(colOnline, y + 7, colOnline + 18, y + 7);
        // Assinatura
        doc.line(colAssinatura - 10, y + 7, pageWidth - margin - 3, y + 7);
        
        y += lineHeight;
      });
      
      // Rodapé
      y += 10;
      if (y < pageHeight - 30) {
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Total de Participantes: ${dados.total_participantes}`, margin, y);
        doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageWidth - margin - 50, y);
      }
      
      // Salvar PDF
      const nomeArquivo = `Lista_Presenca_${dados.turma.nome?.replace(/[^a-zA-Z0-9]/g, '_') || 'turma'}.pdf`;
      doc.save(nomeArquivo);
      
      toast.success('Lista de presença gerada com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar lista de presença');
    }
  };
  
  // Funções para gerenciar questões
  const handleAddQuestao = () => {
    setEditingQuestao(null);
    setQuestaoForm({
      pergunta: '',
      alternativas: ['', '', '', ''],
      resposta_correta: 0
    });
    setShowQuestoesModal(true);
  };
  
  const handleEditQuestao = (index) => {
    const q = editForm.questoes[index];
    setEditingQuestao(index);
    setQuestaoForm({
      pergunta: q.pergunta,
      alternativas: [...q.alternativas],
      resposta_correta: q.resposta_correta
    });
    setShowQuestoesModal(true);
  };
  
  const handleSaveQuestao = () => {
    if (!questaoForm.pergunta.trim()) {
      toast.error('Digite a pergunta');
      return;
    }
    if (questaoForm.alternativas.filter(a => a.trim()).length < 2) {
      toast.error('Adicione pelo menos 2 alternativas');
      return;
    }
    
    const newQuestoes = [...editForm.questoes];
    const questao = {
      pergunta: questaoForm.pergunta,
      alternativas: questaoForm.alternativas.filter(a => a.trim()),
      resposta_correta: questaoForm.resposta_correta
    };
    
    if (editingQuestao !== null) {
      newQuestoes[editingQuestao] = questao;
    } else {
      newQuestoes.push(questao);
    }
    
    setEditForm({ ...editForm, questoes: newQuestoes });
    setShowQuestoesModal(false);
  };
  
  const handleRemoveQuestao = (index) => {
    const newQuestoes = editForm.questoes.filter((_, i) => i !== index);
    setEditForm({ ...editForm, questoes: newQuestoes });
  };

  const fetchEmployees = async () => {
    try {
      const res = await employeesAPI.getAll({ situacao: 'ativo' });
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleOpenAddParticipant = () => {
    fetchEmployees();
    setSelectedEmployees([]);
    setShowAddParticipant(true);
  };

  const handleAddParticipants = async () => {
    if (selectedEmployees.length === 0) {
      toast.warning('Selecione pelo menos um funcionário');
      return;
    }
    
    setAddingParticipant(true);
    let added = 0;
    
    for (const empId of selectedEmployees) {
      try {
        await turmasAPI.addParticipante(selectedTurma.id, { employee_id: empId });
        added++;
      } catch (err) {
        console.error('Error adding:', empId, err);
      }
    }
    
    toast.success(`${added} participante(s) adicionado(s)`);
    setShowAddParticipant(false);
    fetchTurmaDetail(selectedTurma);
    setAddingParticipant(false);
  };

  const handleRemoveParticipant = async (employeeId) => {
    try {
      await turmasAPI.removeParticipante(selectedTurma.id, employeeId);
      toast.success('Participante removido');
      fetchTurmaDetail(selectedTurma);
    } catch (error) {
      toast.error('Erro ao remover participante');
    }
  };

  const handleTogglePresente = (employeeId) => {
    setPresentes(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleConcludeTurma = async () => {
    if (presentes.length === 0) {
      toast.warning('Marque pelo menos um participante como presente');
      return;
    }
    
    setConcluding(true);
    try {
      const res = await turmasAPI.concluir(selectedTurma.id, {
        presentes_ids: presentes,
        data_conclusao: new Date().toISOString()
      });
      toast.success(res.data.message);
      setShowConcludeModal(false);
      setShowDetailModal(false);
      fetchTurmas();
    } catch (error) {
      toast.error('Erro ao concluir turma');
    } finally {
      setConcluding(false);
    }
  };

  const filteredTurmas = turmas.filter(t => {
    const matchSearch = !search || 
      t.nome?.toLowerCase().includes(search.toLowerCase()) ||
      t.training_nome?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar turma..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Status</SelectItem>
            <SelectItem value="aberta">Abertas</SelectItem>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="concluida">Concluídas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-900">{turmas.length}</div>
            <div className="text-sm text-slate-500">Total de Turmas</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {turmas.filter(t => t.status === 'aberta').length}
            </div>
            <div className="text-sm text-slate-500">Abertas</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {turmas.filter(t => t.status === 'em_andamento').length}
            </div>
            <div className="text-sm text-slate-500">Em Andamento</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-600">
              {turmas.filter(t => t.status === 'concluida').length}
            </div>
            <div className="text-sm text-slate-500">Concluídas</div>
          </CardContent>
        </Card>
      </div>

      {/* Turmas List */}
      {filteredTurmas.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">Nenhuma turma encontrada</h3>
            <p className="text-slate-500 mt-2">
              Crie novas turmas através do cadastro de treinamentos
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredTurmas.map((turma) => (
            <Card key={turma.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-slate-900">{turma.nome}</h3>
                      <Badge className={STATUS_COLORS[turma.status] || 'bg-slate-100'}>
                        {STATUS_LABELS[turma.status] || turma.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      Treinamento: {turma.training_nome}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {turma.data_inicio ? new Date(turma.data_inicio).toLocaleDateString('pt-BR') : 'A definir'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {turma.participantes_count || 0}/{turma.capacidade_maxima || 30} participantes
                      </span>
                      {turma.instrutor && (
                        <span>Instrutor: {turma.instrutor}</span>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fetchTurmaDetail(turma)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Gerenciar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-600" />
              {selectedTurma?.nome}
            </DialogTitle>
            <DialogDescription>
              {selectedTurma?.training_nome}
            </DialogDescription>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
            </div>
          ) : turmaDetail ? (
            <div className="space-y-6">
              {/* Info Header with Edit Button */}
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-slate-700">Informações da Turma</h4>
                {!isEditing && (
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    {turmaDetail.status === 'concluida' ? 'Editar Detalhes' : 'Editar'}
                  </Button>
                )}
                {isEditing && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                    <Button size="sm" onClick={handleSaveEdit} disabled={savingEdit}>
                      {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Salvar
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Aviso para turma concluída */}
              {turmaDetail.status === 'concluida' && isEditing && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Editando turma concluída:</strong> Você pode alterar informações administrativas como instrutor, data e valores. 
                    O histórico de presença e conclusões não será afetado.
                  </p>
                </div>
              )}

              {/* Info - View or Edit Mode */}
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border-2 border-cyan-200">
                  <div>
                    <Label className="text-xs text-slate-500">Data do Treinamento</Label>
                    <Input
                      type="date"
                      value={editForm.data_inicio}
                      onChange={(e) => setEditForm({ ...editForm, data_inicio: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Data de Término</Label>
                    <Input
                      type="date"
                      value={editForm.data_fim}
                      onChange={(e) => setEditForm({ ...editForm, data_fim: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Instrutor / Facilitador</Label>
                    <Input
                      value={editForm.instrutor}
                      onChange={(e) => setEditForm({ ...editForm, instrutor: e.target.value })}
                      placeholder="Nome do instrutor"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Vagas</Label>
                    <div className="font-medium mt-2">
                      {turmaDetail.participantes_count}/{turmaDetail.capacidade_maxima}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Valor do Treinamento (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.valor_treinamento}
                      onChange={(e) => setEditForm({ ...editForm, valor_treinamento: e.target.value })}
                      placeholder="Ex: 1500.00"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Valor/Hora Total dos Empregados (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.valor_hora_total}
                      onChange={(e) => setEditForm({ ...editForm, valor_hora_total: e.target.value })}
                      placeholder="Ex: 2500.00"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-400 mt-1">Custo total das horas dos participantes durante o treinamento</p>
                  </div>
                  
                  {/* Seção de Treinamento Online */}
                  <div className="col-span-2 border-t pt-4 mt-4">
                    <h5 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      Treinamento Online
                    </h5>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Modalidade</Label>
                        <Select
                          value={editForm.modalidade}
                          onValueChange={(v) => setEditForm({ ...editForm, modalidade: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="presencial">Presencial</SelectItem>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="hibrido">Híbrido</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {(editForm.modalidade === 'online' || editForm.modalidade === 'hibrido') && (
                        <>
                          <div>
                            <Label className="text-xs text-slate-500">URL do Vídeo (YouTube)</Label>
                            <Input
                              value={editForm.video_youtube_url}
                              onChange={(e) => setEditForm({ ...editForm, video_youtube_url: e.target.value })}
                              placeholder="https://youtube.com/watch?v=..."
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">% Mínimo do Vídeo</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={editForm.percentual_minimo}
                              onChange={(e) => setEditForm({ ...editForm, percentual_minimo: parseInt(e.target.value) || 80 })}
                              className="mt-1"
                            />
                            <p className="text-xs text-slate-400 mt-1">Percentual do vídeo para liberar a prova</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Prova de Validação</Label>
                            <div className="flex items-center gap-2 mt-2">
                              <input
                                type="checkbox"
                                checked={editForm.prova_ativa}
                                onChange={(e) => setEditForm({ ...editForm, prova_ativa: e.target.checked })}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Ativar prova</span>
                            </div>
                          </div>
                          
                          {editForm.prova_ativa && (
                            <>
                              <div>
                                <Label className="text-xs text-slate-500">Nota Mínima (%)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={editForm.nota_minima}
                                  onChange={(e) => setEditForm({ ...editForm, nota_minima: parseInt(e.target.value) || 70 })}
                                  className="mt-1"
                                />
                              </div>
                              <div className="col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs text-slate-500">Questões da Prova ({editForm.questoes.length})</Label>
                                  <Button variant="outline" size="sm" onClick={handleAddQuestao}>
                                    <Plus className="w-4 h-4 mr-1" />
                                    Adicionar
                                  </Button>
                                </div>
                                {editForm.questoes.length > 0 && (
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {editForm.questoes.map((q, idx) => (
                                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="text-sm truncate flex-1">{idx + 1}. {q.pergunta}</span>
                                        <div className="flex gap-1">
                                          <Button variant="ghost" size="sm" onClick={() => handleEditQuestao(idx)}>
                                            <Edit className="w-3 h-3" />
                                          </Button>
                                          <Button variant="ghost" size="sm" onClick={() => handleRemoveQuestao(idx)} className="text-red-500">
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <div className="text-xs text-slate-500">Status</div>
                      <Badge className={STATUS_COLORS[turmaDetail.status]}>
                        {STATUS_LABELS[turmaDetail.status]}
                      </Badge>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">
                        {turmaDetail.status === 'concluida' ? 'Data de Realização' : 'Data do Treinamento'}
                      </div>
                      <div className="font-medium">
                        {turmaDetail.status === 'concluida' && turmaDetail.data_conclusao
                          ? new Date(turmaDetail.data_conclusao).toLocaleDateString('pt-BR')
                          : turmaDetail.data_inicio 
                            ? new Date(turmaDetail.data_inicio).toLocaleDateString('pt-BR') 
                            : '-'}
                        {turmaDetail.data_fim && turmaDetail.data_fim !== turmaDetail.data_inicio && (
                          <span className="text-xs text-slate-400 ml-1">
                            (período: {new Date(turmaDetail.data_inicio).toLocaleDateString('pt-BR')} - {new Date(turmaDetail.data_fim).toLocaleDateString('pt-BR')})
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Instrutor</div>
                      <div className="font-medium">{turmaDetail.instrutor || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Vagas</div>
                      <div className="font-medium">
                        {turmaDetail.participantes_count}/{turmaDetail.capacidade_maxima}
                      </div>
                    </div>
                  </div>

                  {/* Custos Section */}
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Custos do Treinamento
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs text-emerald-600">Valor do Treinamento</div>
                        <div className="text-lg font-semibold text-emerald-900">
                          {turmaDetail.valor_treinamento 
                            ? `R$ ${turmaDetail.valor_treinamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` 
                            : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-emerald-600">Valor/Hora dos Empregados</div>
                        <div className="text-lg font-semibold text-emerald-900">
                          {turmaDetail.valor_hora_total 
                            ? `R$ ${turmaDetail.valor_hora_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` 
                            : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-emerald-600">Custo Total Estimado</div>
                        <div className="text-lg font-bold text-emerald-900">
                          {(turmaDetail.valor_treinamento || turmaDetail.valor_hora_total)
                            ? `R$ ${((turmaDetail.valor_treinamento || 0) + (turmaDetail.valor_hora_total || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                            : '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Participants */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-slate-900">Participantes</h4>
                  {turmaDetail.status !== 'concluida' && (
                    <Button size="sm" onClick={handleOpenAddParticipant}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Adicionar
                    </Button>
                  )}
                </div>

                {turmaDetail.participantes?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Cargo</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {turmaDetail.participantes.map((p) => (
                        <TableRow key={p.employee_id}>
                          <TableCell className="font-medium">{p.employee_nome}</TableCell>
                          <TableCell>{p.employee_cargo || '-'}</TableCell>
                          <TableCell>
                            <Badge className={PARTICIPANTE_STATUS_COLORS[p.status]}>
                              {p.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {turmaDetail.status !== 'concluida' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveParticipant(p.employee_id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    Nenhum participante inscrito
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fechar
            </Button>
            {turmaDetail?.participantes?.length > 0 && (
              <Button 
                variant="outline"
                onClick={handleGenerateListaPresenca}
              >
                <FileText className="w-4 h-4 mr-2" />
                Lista de Presença
              </Button>
            )}
            {turmaDetail?.status !== 'concluida' && turmaDetail?.participantes?.length > 0 && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setShowConcludeModal(true)}
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Concluir Turma
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Participant Modal */}
      <Dialog open={showAddParticipant} onOpenChange={setShowAddParticipant}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Participantes</DialogTitle>
            <DialogDescription>
              Selecione os funcionários para inscrever na turma
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Buscar funcionário..."
              onChange={(e) => {
                // Could add search filter here
              }}
            />

            <div className="max-h-64 overflow-y-auto space-y-2">
              {employees
                .filter(emp => !turmaDetail?.participantes?.some(p => p.employee_id === emp.id))
                .map((emp) => (
                  <label 
                    key={emp.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedEmployees.includes(emp.id)}
                      onCheckedChange={(checked) => {
                        setSelectedEmployees(prev => 
                          checked 
                            ? [...prev, emp.id]
                            : prev.filter(id => id !== emp.id)
                        );
                      }}
                    />
                    <div>
                      <div className="font-medium">{emp.nome}</div>
                      <div className="text-sm text-slate-500">{emp.cargo}</div>
                    </div>
                  </label>
                ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddParticipant(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddParticipants}
              disabled={addingParticipant || selectedEmployees.length === 0}
            >
              {addingParticipant && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Adicionar ({selectedEmployees.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conclude Turma Modal */}
      <Dialog open={showConcludeModal} onOpenChange={setShowConcludeModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCheck className="w-5 h-5 text-green-600" />
              Concluir Turma
            </DialogTitle>
            <DialogDescription>
              Marque os participantes que estiveram presentes. Os treinamentos serão registrados automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Atenção</span>
              </div>
              <p className="text-sm text-amber-600 mt-1">
                Esta ação não pode ser desfeita. Os participantes marcados como presentes terão o treinamento registrado em seu histórico.
              </p>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {turmaDetail?.participantes?.map((p) => (
                <label 
                  key={p.employee_id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    presentes.includes(p.employee_id) 
                      ? 'bg-green-50 border-green-200' 
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <Checkbox
                    checked={presentes.includes(p.employee_id)}
                    onCheckedChange={() => handleTogglePresente(p.employee_id)}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{p.employee_nome}</div>
                    <div className="text-sm text-slate-500">{p.employee_cargo}</div>
                  </div>
                  {presentes.includes(p.employee_id) ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <X className="w-5 h-5 text-slate-300" />
                  )}
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <span className="text-sm text-slate-600">
                Presentes: <strong>{presentes.length}</strong>
              </span>
              <span className="text-sm text-slate-600">
                Ausentes: <strong>{(turmaDetail?.participantes?.length || 0) - presentes.length}</strong>
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConcludeModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConcludeTurma}
              disabled={concluding || presentes.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {concluding && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Concluir e Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Questões */}
      <Dialog open={showQuestoesModal} onOpenChange={setShowQuestoesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingQuestao !== null ? 'Editar Questão' : 'Nova Questão'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Pergunta</Label>
              <Input
                value={questaoForm.pergunta}
                onChange={(e) => setQuestaoForm({ ...questaoForm, pergunta: e.target.value })}
                placeholder="Digite a pergunta..."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Alternativas</Label>
              <div className="space-y-2 mt-1">
                {questaoForm.alternativas.map((alt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="resposta_correta"
                      checked={questaoForm.resposta_correta === idx}
                      onChange={() => setQuestaoForm({ ...questaoForm, resposta_correta: idx })}
                      className="w-4 h-4"
                    />
                    <Input
                      value={alt}
                      onChange={(e) => {
                        const newAlts = [...questaoForm.alternativas];
                        newAlts[idx] = e.target.value;
                        setQuestaoForm({ ...questaoForm, alternativas: newAlts });
                      }}
                      placeholder={`Alternativa ${idx + 1}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Selecione a alternativa correta marcando o círculo</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestoesModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveQuestao}>
              Salvar Questão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
